/**
 * Question speech-bubble for MCQ (§7) — web port of the iOS MCQ bubble in
 * `client-native/.../components/PersonalizedMCQ.tsx:143-161`.
 *
 * A rounded card (radius 24, padding 20×32, soft shadow) with a down-pointing
 * `BubbleTail` over a `parrot-sitting.png` image. Feed bubble bg = greyLightest
 * (#f5f5f5), lesson bg = white. MCQ passes the parrot via `parrotImageSrc`
 * (`@/assets/parrot-sitting.png`); if omitted the bubble degrades gracefully.
 */
import type { CSSProperties } from "react";
import { colors, fontFamily } from "@/ui";
import type { ActivityVariant } from "../types";

export interface ParrotBubbleProps {
  /** The question text shown inside the bubble. */
  children: React.ReactNode;
  variant?: ActivityVariant;
  /** Optional parrot image URL; omitted (asset missing) → renders without it. */
  parrotImageSrc?: string;
}

const TAIL_SIZE = 14;

/** CSS down-pointing triangle that visually continues the bubble. */
function BubbleTail({ color }: { color: string }) {
  const style: CSSProperties = {
    width: 0,
    height: 0,
    borderLeft: `${TAIL_SIZE}px solid transparent`,
    borderRight: `${TAIL_SIZE}px solid transparent`,
    borderTop: `${TAIL_SIZE}px solid ${color}`,
    marginTop: -1,
  };
  return <div aria-hidden style={style} />;
}

export default function ParrotBubble({
  children,
  variant = "lesson",
  parrotImageSrc,
}: ParrotBubbleProps) {
  const bubbleBg = variant === "feed" ? colors.greyLightest : colors.white;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div
        style={{
          alignSelf: "center",
          filter: "drop-shadow(0 3px 10px rgba(0,0,0,0.08))",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div
          style={{
            backgroundColor: bubbleBg,
            borderRadius: 24,
            padding: "20px 32px",
            color: colors.primary,
            fontFamily,
            fontWeight: 600,
            fontSize: "1.5rem",
            lineHeight: 1.25,
            textAlign: "center",
            maxWidth: "100%",
            overflowWrap: "anywhere",
          }}
        >
          {children}
        </div>
        <BubbleTail color={bubbleBg} />
      </div>
      {parrotImageSrc ? (
        <img
          src={parrotImageSrc}
          alt=""
          aria-hidden
          style={{
            width: "min(180px, 40vw)",
            height: "min(180px, 40vw)",
            marginTop: 2,
            objectFit: "contain",
          }}
        />
      ) : null}
    </div>
  );
}
